import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import SectionMarker from '@/components/sections/SectionMarker';
import PageHero from '@/components/sections/PageHero';
import { NOW_PAGE, PHOTOS } from '@/constants/content';
import { NOW } from '@/constants/testIds';
import { usePageMeta } from '@/lib/usePageMeta';
import { SEO } from '@/constants/seo';

export default function Now() {
  usePageMeta({ ...SEO.now });
  const n = NOW_PAGE;
  return (
    <div className="bg-ivory" data-testid={NOW.root}>
      <section className="pt-40 lg:pt-56 pb-16 lg:pb-24">
        <div className="mx-auto max-w-[1200px] px-6 sm:px-10 lg:px-16">
          <div className="flex items-center gap-4 mb-8">
            <span className="w-2 h-2 rounded-full bg-emerald animate-pulse" aria-hidden />
            <span className="font-label text-[11px] tracking-widest uppercase text-matte/60">Last Updated · {n.lastUpdated}</span>
          </div>
          <h1 className="font-serif-display text-6xl md:text-7xl lg:text-[8rem] leading-[0.94] tracking-tightest text-matte">
            Now.
          </h1>
          <p className="mt-10 text-matte/75 leading-relaxed text-[15px] max-w-2xl">
            {n.intro}
          </p>
        </div>
      </section>

      <section className="pb-16">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16">
          <PageHero
            src={PHOTOS.nowPage}
            alt="Toia Wallace, now"
            mobileAnchor="top"
            desktopAnchor="center"
            desktopHeight="md:h-[50vh] lg:h-[70vh]"
            testId="now-hero-image"
          />
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="mx-auto max-w-[1200px] px-6 sm:px-10 lg:px-16">
          <SectionMarker number="01" label="Active" className="mb-10" />
          <div className="grid md:grid-cols-3 gap-6">
            {n.currentlyBuilding.map((c, i) => {
              const inner = (
                <>
                  <div className="flex items-start justify-between mb-4">
                    <div className="eyebrow text-taupe">{String(i + 1).padStart(2, '0')}</div>
                    {c.url && (
                      <ArrowUpRight size={18} strokeWidth={1.2} className="text-matte/40 group-hover:text-gold transition-colors duration-500" />
                    )}
                  </div>
                  <h3 className="font-serif-display text-3xl tracking-tighter text-matte leading-tight mb-3 group-hover:text-gold transition-colors duration-500">{c.name}</h3>
                  <p className="text-sm text-matte/70 leading-relaxed">{c.body}</p>
                </>
              );
              const base = 'group block bg-white rounded-2xl p-8 border border-taupe/20 hover:border-gold/40 hover:-translate-y-1 transition-[border-color,transform] duration-500';
              if (c.url) {
                return (
                  <a
                    key={c.name}
                    href={c.url}
                    target="_blank"
                    rel="noreferrer"
                    className={base}
                    data-testid={`now-active-${i}`}
                  >
                    {inner}
                  </a>
                );
              }
              return (
                <article key={c.name} className={base}>{inner}</article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-white">
        <div className="mx-auto max-w-[1200px] px-6 sm:px-10 lg:px-16">
          <SectionMarker number="02" label="Building Next" className="mb-10" />
          <div className="grid md:grid-cols-2 gap-6">
            {n.buildingNext.map((c, i) => (
              <article key={c.name} className="bg-ivory rounded-2xl p-10 border border-taupe/20 hover:border-gold/40 transition-colors duration-500">
                <div className="eyebrow text-taupe mb-4">{String(i + 1).padStart(2, '0')}</div>
                <h3 className="font-serif-display text-4xl tracking-tighter text-matte leading-tight mb-3">{c.name}</h3>
                <p className="text-sm text-matte/70 leading-relaxed">{c.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="mx-auto max-w-[1200px] px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-16 items-start">
          <div className="lg:col-span-5">
            <SectionMarker number="03" label="Speaking" className="mb-8" />
            <h2 className="font-serif-display text-4xl md:text-5xl tracking-tighter leading-tight text-matte">
              Currently accepting invitations for:
            </h2>
          </div>
          <div className="lg:col-span-7">
            <ul className="grid sm:grid-cols-2 gap-x-8 mb-10">
              {n.speaking.map((s, i) => (
                <li key={s} className="border-t border-taupe/25 py-5 flex items-baseline gap-6">
                  <span className="font-label text-[11px] tracking-widest uppercase text-taupe">{String(i + 1).padStart(2, '0')}</span>
                  <span className="font-serif-display text-2xl text-matte tracking-tight">{s}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/speaking#inquiry"
              data-testid={NOW.ctaSpeaking}
              className="btn-primary"
            >
              Book Toia Wallace
              <ArrowUpRight size={14} strokeWidth={1.6} />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-sand/40">
        <div className="mx-auto max-w-[1200px] px-6 sm:px-10 lg:px-16 grid md:grid-cols-2 gap-12 lg:gap-16">
          <div>
            <SectionMarker number="04" label="Learning" className="mb-8" />
            <ul className="space-y-3">
              {n.learning.map((l) => (
                <li key={l} className="font-serif-display text-2xl text-matte tracking-tight border-b border-taupe/25 pb-4">{l}</li>
              ))}
            </ul>
          </div>
          <div>
            <SectionMarker number="05" label="Reading" className="mb-8" />
            <ul className="space-y-3">
              {n.reading.map((l) => (
                <li key={l} className="font-serif-display text-2xl text-matte tracking-tight border-b border-taupe/25 pb-4">{l}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-white">
        <div className="mx-auto max-w-[1200px] px-6 sm:px-10 lg:px-16">
          <SectionMarker number="06" label="Upcoming" className="mb-10" />
          <div className="space-y-1">
            {n.upcoming.map((u, i) => (
              <div key={u} className="border-t border-taupe/25 py-6 flex items-baseline gap-8">
                <span className="font-label text-[11px] tracking-widest uppercase text-taupe">{String(i + 1).padStart(2, '0')}</span>
                <span className="font-serif-display text-2xl md:text-3xl text-matte tracking-tight leading-snug">{u}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
